import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import {
  ResumePDFLink,
  ResumePDFText,
} from "components/Resume/ResumePDF/common";
import { handleClick } from "lib/utils/navigator";
import {
  useHighlight,
  shouldHighlightText,
  isHighlightTarget,
  getMergedHighlightStyle,
} from "components/Resume/HighlightContext";
import { useMistakeAnalysis, isSentenceMistake, isSingleWordMistake } from "components/Resume/MistakeAnalysisContext";

const MISTAKE_TEXT_COLOR = "#dc2626";

export const TemplateTwelvePDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(
    () => handleClick(heading),
    [heading]
  );
  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        gap: spacing["2"],
        marginTop: spacing["5"],
        cursor: "pointer",
        // ...style,
      }}
    >
      {heading && (
        <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
              color: themeColor,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
          {themeColor && (
            <View
              style={{
                height: "3pt",
                width: "100%",
                backgroundColor: themeColor,
                marginRight: spacing["3.5"],
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            />
          )}
        </View>
      )}
      {children}
    </div>
  );
};

export const ResumePDFDescriptionWithWhiteFont = ({
  items,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section = "experience",
  itemIndex = 0,
  themeColor,
}: {
  items: string[];
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number | null;
  themeColor?: string;
}) => {
  const highlight = useHighlight();
  const mistakeAnalysis = useMistakeAnalysis();
  // Check if items is defined and has at least one element
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {enableHighlight
            ? renderNodeWithHighlight(node, section, itemIndex, themeColor , highlight, enableMistakeHighlight, mistakeAnalysis)
            : renderNode(node)}
        </React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText
              key={Math.random()}
              underline={true}
              themeColor="white"
            >
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText themeColor="white">
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4, // adjust spacing as needed
                  paddingRight: 4, // adjust spacing as needed
                  lineHeight: 1.3,
                }}
                bold={true}
                themeColor="white"
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                themeColor="white"
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (
  node: Node,
  themeColor: string = "black"
): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        return (
          <View>
            <ResumePDFText
              style={{ lineHeight: 1.3, flexGrow: 1 }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );

      case "ol":
        return (
          <View style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                    themeColor={themeColor}
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );

      case "li":
        return (
          <View style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );

      default:
        return null;
    }
  }
  return null;
};

export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

const renderNodeWithHighlight = (
  node: Node,
  currentSection: string,
  currentItemIndex: number | null,
  themeColor: string = "black",
  highlight: ReturnType<typeof useHighlight>,
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode => {
  const isTarget = isHighlightTarget(
    currentSection,
    currentItemIndex,
    highlight
  );
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const textContent = element.textContent || "";
    const nodeName = element.nodeName.toLowerCase();
    const shouldHighlight =
      isTarget &&
      highlight.enabled &&
      highlight.mode === "sentence" &&
      shouldHighlightText(textContent, highlight.texts, highlight.mode);
    switch (nodeName) {
      case "p":
        return (
          <View key={Math.random()}>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                ...(shouldHighlight
                  ? {
                      backgroundColor: highlight.style.backgroundColor,
                      color:
                        highlight.style.color ||
                        highlight.style.textDecorationColor,
                      fontWeight: highlight.style.fontWeight as any,
                      textDecoration: highlight.style.textDecoration as any,
                      textDecorationStyle: highlight.style
                        .textDecorationStyle as any,
                      textDecorationColor: highlight.style
                        .textDecorationColor as any,
                    }
                  : {}),
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTMLWithHighlight(
                element.innerHTML,
                currentSection,
                currentItemIndex,
                themeColor,
                highlight,
                enableMistakeHighlight,
                mistakeAnalysis
              )}
              <br />
            </ResumePDFText>
          </View>
        );
      case "ol":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                    themeColor={themeColor}
                  >
                    {parseAndTransformHTMLWithHighlight(
                      (child as Element).innerHTML,
                      currentSection,
                      currentItemIndex,
                      themeColor,
                      highlight,
                      enableMistakeHighlight,
                      mistakeAnalysis
                    )}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
      case "li":
        const liShouldHighlight =
          isTarget &&
          highlight.enabled &&
          highlight.mode === "sentence" &&
          shouldHighlightText(textContent, highlight.texts, highlight.mode);
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
                ...(liShouldHighlight
                  ? {
                      backgroundColor: highlight.style.backgroundColor,
                      color:
                        highlight.style.color ||
                        highlight.style.textDecorationColor,
                      fontWeight: highlight.style.fontWeight as any,
                      textDecoration: highlight.style.textDecoration as any,
                      textDecorationStyle: highlight.style
                        .textDecorationStyle as any,
                      textDecorationColor: highlight.style
                        .textDecorationColor as any,
                    }
                  : {}),
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTMLWithHighlight(
                element.innerHTML,
                currentSection,
                currentItemIndex,
                themeColor,
                highlight,
                enableMistakeHighlight,
                mistakeAnalysis
              )}
            </ResumePDFText>
          </View>
        );
      default:
        return null;
    }
  }
  return null;
};

const parseAndTransformHTMLWithHighlight = (
  htmlString: string,
  currentSection: string,
  currentItemIndex: number | null,
  themeColor: string = "black",
  highlight: ReturnType<typeof useHighlight>,
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];
  const isTarget = isHighlightTarget(
    currentSection,
    currentItemIndex,
    highlight
  );
  const transformNode = (
    node: Node,
    existingStyles: {
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
    } = {}
  ) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const nodeName = element.nodeName.toLowerCase();
      let newStyles = { ...existingStyles };
      switch (nodeName) {
        case "strong":
        case "b":
          newStyles.bold = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
        case "em":
        case "i":
          newStyles.italic = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
        case "u":
          newStyles.underline = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {Array.from(element.childNodes).map((child, idx) => (
                  <React.Fragment key={`${Math.random()}-${idx}`}>
                    {transformNode(child, { ...newStyles, underline: true })}
                  </React.Fragment>
                ))}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
        default:
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      if (!text.trim()) return text;
      
      // Check if entire text is a sentence mistake
      const isSentenceError = enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null && 
        isSentenceMistake(text, currentSection, currentItemIndex, mistakeAnalysis.mistakeData);

      if (isSentenceError) {
        // Text is a sentence mistake - handle hover interactions
        const isWordHover = isTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0;
        const isSentenceHover = isTarget && highlight.enabled && highlight.mode === 'sentence' && highlight.texts.length > 0 &&
          shouldHighlightText(text, highlight.texts, 'sentence');

        if (isWordHover) {
          // Sentence mistake + word hover
          return renderTextWithWordHighlights(
            text, 
            highlight, 
            existingStyles,
            themeColor,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            true,  // isHoverTarget
            enableMistakeHighlight,
            true   // forceRedText
          );
        } else if (isSentenceHover) {
          // Sentence mistake + sentence hover
          const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, true);
          return (
            <ResumePDFText key={Math.random()} style={{ ...mergedStyle, color: MISTAKE_TEXT_COLOR }} themeColor={themeColor}>
              {text}
            </ResumePDFText>
          );
        } else {
          // Sentence mistake, no hover
          return (
            <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor={themeColor}>
              {text}
            </ResumePDFText>
          );
        }
      }

      // Check if text needs word-level mistake processing
      const needsWordProcessing = enableMistakeHighlight && mistakeAnalysis && currentSection && currentItemIndex !== null;
      
      if (!isTarget || !highlight.enabled) {
        if (needsWordProcessing) {
          // Word-level mistake detection without hover
          return renderTextWithWordHighlights(
            text,
            highlight,
            existingStyles,
            themeColor,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            false,  // isHoverTarget
            enableMistakeHighlight,
            false   // forceRedText
          );
        }
        
        if (
          existingStyles.bold ||
          existingStyles.italic ||
          existingStyles.underline
        ) {
          return (
            <ResumePDFText
              key={Math.random()}
              bold={existingStyles.bold}
              italic={existingStyles.italic}
              underline={existingStyles.underline}
              themeColor={themeColor}
            >
              {text}
            </ResumePDFText>
          );
        }
        return text;
      }
      
      if (highlight.mode === "word" && highlight.texts.length > 0) {
        return renderTextWithWordHighlights(
          text,
          highlight,
          existingStyles,
          themeColor,
          currentSection,
          currentItemIndex,
          mistakeAnalysis,
          true,  // isHoverTarget
          enableMistakeHighlight,
          false  // forceRedText
        );
      }
      
      if (highlight.mode === "sentence" && highlight.texts.length > 0) {
        const shouldHighlight = shouldHighlightText(
          text,
          highlight.texts,
          "sentence"
        );
        
        if (needsWordProcessing) {
          // Sentence hover + word-level mistakes
          return renderTextWithWordHighlights(
            text,
            highlight,
            existingStyles,
            themeColor,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            shouldHighlight,  // isHoverTarget
            enableMistakeHighlight,
            false  // forceRedText
          );
        }
        
        const mergedStyle = getMergedHighlightStyle(
          existingStyles,
          highlight.style,
          shouldHighlight
        );
        return (
          <ResumePDFText
            key={Math.random()}
            style={mergedStyle}
            themeColor={themeColor}
          >
            {text}
          </ResumePDFText>
        );
      }
      
      if (
        existingStyles.bold ||
        existingStyles.italic ||
        existingStyles.underline
      ) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor={themeColor}
          >
            {text}
          </ResumePDFText>
        );
      }
      return text;
    }
    return null;
  };
  doc.body.childNodes.forEach((node) => {
    const transformed = transformNode(node);
    if (transformed) {
      transformedChildren.push(transformed);
    }
  });
  return transformedChildren;
};

const renderTextWithWordHighlights = (
  text: string,
  highlight: ReturnType<typeof useHighlight>,
  existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean },
  themeColor: string = "black",
  currentSection?: string,
  currentItemIndex?: number | null,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>,
  isHoverTarget: boolean = false,
  enableMistakeHighlight: boolean = false,
  forceRedText: boolean = false
): React.ReactNode => {
  try {
    const words = highlight.texts
      .map((w: string) => (w || "").trim())
      .filter(Boolean)
      .map((w: string) => {
        const escaped = w.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&");
        return escaped;
      })
      .join("|");
    if (!words) {
      // No hover highlights, check for mistakes only
      if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection && currentItemIndex !== null && !forceRedText) {
        const textWords = text.split(/\s+/).filter(Boolean);
        const hasAnyMistake = textWords.some(word =>
          isSingleWordMistake(word, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData!)
        );

        if (hasAnyMistake) {
          return (
            <>
              {textWords.map((word, i) => {
                const isMistake = isSingleWordMistake(word, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData!);
                const mergedStyle = isMistake
                  ? { ...existingStyles, color: MISTAKE_TEXT_COLOR }
                  : existingStyles;
                
                return (
                  <React.Fragment key={i}>
                    {i > 0 && " "}
                    {(mergedStyle.bold || mergedStyle.italic || mergedStyle.underline || isMistake) ? (
                      <ResumePDFText
                        bold={mergedStyle.bold}
                        italic={mergedStyle.italic}
                        underline={mergedStyle.underline}
                        style={isMistake ? { color: MISTAKE_TEXT_COLOR } : {}}
                        themeColor={themeColor}
                      >
                        {word}
                      </ResumePDFText>
                    ) : (
                      word
                    )}
                  </React.Fragment>
                );
              })}
            </>
          );
        }
      }
      
      if (forceRedText) {
        return (
          <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor={themeColor}>
            {text}
          </ResumePDFText>
        );
      }
      
      if (
        existingStyles.bold ||
        existingStyles.italic ||
        existingStyles.underline
      ) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor={themeColor}
          >
            {text}
          </ResumePDFText>
        );
      }
      return text;
    }
    
    // STEP 1: Split by hover words regex
    const re = new RegExp(`\\b(${words})\\b`, "giu");
    const parts: Array<{ text: string; isHoverMatch: boolean }> = [];
    let lastIndex = 0;
    let match;
    
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.substring(lastIndex, match.index), isHoverMatch: false });
      }
      parts.push({ text: match[0], isHoverMatch: true });
      lastIndex = re.lastIndex;
    }
    
    if (lastIndex < text.length) {
      parts.push({ text: text.substring(lastIndex), isHoverMatch: false });
    }
    
    if (parts.length === 0) {
      parts.push({ text, isHoverMatch: false });
    }
    
    // STEP 2: For each part, check for mistakes
    const finalParts: Array<{ text: string; isHoverMatch: boolean; isMistake: boolean }> = [];
    
    for (const part of parts) {
      if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection && currentItemIndex !== null && !part.isHoverMatch && part.text.trim()) {
        // Non-hover part: split by whitespace to check each word
        const wordParts = part.text.split(/(\s+)/);
        
        for (const wordPart of wordParts) {
          if (!wordPart) continue;
          
          const trimmed = wordPart.trim();
          const isMistake = trimmed.length > 0 && isSingleWordMistake(
            trimmed,
            currentSection,
            currentItemIndex ?? null,
            mistakeAnalysis.mistakeData
          );
          
          finalParts.push({
            text: wordPart,
            isHoverMatch: false,
            isMistake
          });
        }
      } else if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection && currentItemIndex !== null && part.isHoverMatch) {
        // Hover match: check if this word is also a mistake
        const isMistake = isSingleWordMistake(
          part.text.trim(),
          currentSection,
          currentItemIndex ?? null,
          mistakeAnalysis.mistakeData
        );
        
        finalParts.push({
          text: part.text,
          isHoverMatch: true,
          isMistake
        });
      } else {
        finalParts.push({
          text: part.text,
          isHoverMatch: part.isHoverMatch,
          isMistake: false
        });
      }
    }
    
    // STEP 3: Render with appropriate styling
    return (
      <>
        {finalParts.map((part, i) => {
          const isRed = forceRedText || part.isMistake;
          
          if (part.isHoverMatch && isHoverTarget) {
            // Hover match: apply yellow background
            const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, true);
            if (isRed) {
              mergedStyle.color = MISTAKE_TEXT_COLOR;
            }
            return (
              <ResumePDFText key={i} style={mergedStyle} themeColor={themeColor}>
                {part.text}
              </ResumePDFText>
            );
          } else if (isRed) {
            // Mistake only: red text
            return (
              <ResumePDFText
                key={i}
                bold={existingStyles.bold}
                italic={existingStyles.italic}
                underline={existingStyles.underline}
                style={{ color: MISTAKE_TEXT_COLOR }}
                themeColor={themeColor}
              >
                {part.text}
              </ResumePDFText>
            );
          } else if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
            // Styling only
            return (
              <ResumePDFText
                key={i}
                bold={existingStyles.bold}
                italic={existingStyles.italic}
                underline={existingStyles.underline}
                themeColor={themeColor}
              >
                {part.text}
              </ResumePDFText>
            );
          } else {
            // Plain text
            return <React.Fragment key={i}>{part.text}</React.Fragment>;
          }
        })}
      </>
    );
  } catch (error) {
    if (forceRedText) {
      return (
        <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor={themeColor}>
          {text}
        </ResumePDFText>
      );
    }
    
    if (
      existingStyles.bold ||
      existingStyles.italic ||
      existingStyles.underline
    ) {
      return (
        <ResumePDFText
          key={Math.random()}
          bold={existingStyles.bold}
          italic={existingStyles.italic}
          underline={existingStyles.underline}
          themeColor={themeColor}
        >
          {text}
        </ResumePDFText>
      );
    }
    return text;
  }
};

export const ResumePDFDescription = ({
  items,
  themeColor,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section = "experience",
  itemIndex = 0,
}: {
  items: string[];
  themeColor?: string;
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number | null;
}) => {
  const highlight = useHighlight();
  const mistakeAnalysis = useMistakeAnalysis();

  if (!items || items.length === 0) {
    return null;
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {enableHighlight
            ? renderNodeWithHighlight(
                node,
                section,
                itemIndex,
                themeColor,
                highlight,
                enableMistakeHighlight,
                mistakeAnalysis
              )
            : renderNode(node, themeColor)}
        </React.Fragment>
      ))}
    </View>
  );
};

// Enhanced version with optimized spacing and orphan heading prevention for TemplateTwelve
export const TemplateTwelvePDFSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(
    () => handleClick(heading),
    [heading]
  );

  // Group content to prevent orphan headings
  const groupedChildren = React.Children.toArray(children);
  const firstChild = groupedChildren[0];
  const restChildren = groupedChildren.slice(1);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        gap: "9pt", // Optimized from spacing["2"] (24pt) to 9pt
        marginTop: "18pt", // Optimized from spacing["5"] (60pt) to 18pt
        cursor: "pointer",
      }}
    >
      {/* Wrap heading with first child only to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: "6pt", // Optimized spacing
          }}
          wrap={false}
        >
          {/* Heading with colored underline */}
          <View style={{ ...styles.flexCol, gap: "3pt" }}>
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white", // White text for visibility on colored background
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "2pt", // Reduced from 3pt
                  width: "100%",
                  backgroundColor: "white", // White underline for visibility
                  marginRight: "12pt", // Reduced from spacing["3.5"]
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
          {/* First child inside the same container */}
          {firstChild}
        </View>
      )}

      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View style={{ ...styles.flexCol, gap: "3pt" }}>
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt",
              color: "white",
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
          {themeColor && (
            <View
              style={{
                height: "2pt",
                width: "100%",
                backgroundColor: "white",
                marginRight: "12pt",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            />
          )}
        </View>
      )}

      {/* Rest of children - can break across pages */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: "9pt",
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};
